import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { P } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { useGo } from '../../../navigation/nav';
import { Avatar, BarChart, Button, Card, Columns, EmptyState, ErrorState, Grid, ListItem, Loading, PageHeader, Screen, StatCard, useTheme } from '../../../ui';

/** Team snapshot from /dashboard/manager. */
export function TeamDashboardScreen() {
  const go = useGo();
  const { can } = useAuth();
  const { colors } = useTheme();
  const q = useQuery({ queryKey: ['dashboard', 'manager'], queryFn: () => api.dashboard.manager() });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data)
    return (
      <Screen>
        <PageHeader title="Team dashboard" />
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </Screen>
    );
  const d = q.data;
  const a = d.attendance_today;
  const canAtt = can(P.ATTENDANCE_TEAM, P.ATTENDANCE_MANAGE);
  const canLeave = can(P.LEAVE_TEAM, P.LEAVE_MANAGE);
  const canPeople = can(P.EMPLOYEES_TEAM);
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="team-dashboard">
      <PageHeader
        title="Team dashboard"
        subtitle={`${d.team_size} people report to you`}
        actions={<Button icon="inbox" variant="primary" title={`Approvals (${d.pending_approvals})`} onPress={() => go('Approvals')} />}
      />
      <Grid min={160}>
        <StatCard label="Team size" value={d.team_size} icon="users" onPress={canPeople ? () => go('TeamProfiles') : undefined} />
        <StatCard label="Pending approvals" value={d.pending_approvals} icon="inbox" tone="warning" onPress={() => go('Approvals')} />
        <StatCard label="Present today" value={a.present} icon="check" tone="success" onPress={canAtt ? () => go('TeamAttendance') : undefined} />
        <StatCard label="On leave today" value={a.on_leave} icon="leaf" tone="info" onPress={canLeave ? () => go('TeamLeave') : undefined} />
      </Grid>
      <Columns>
        <Card title="Attendance today" actions={canAtt ? <Button size="sm" variant="ghost" title="Details" iconRight="chevronRight" onPress={() => go('TeamAttendance')} /> : undefined}>
          <BarChart
            data={[
              { label: 'Present', value: a.present, color: colors.success },
              { label: 'Late', value: a.late, color: colors.warning },
              { label: 'On leave', value: a.on_leave, color: colors.info },
              { label: 'Absent', value: a.absent, color: colors.danger },
              { label: 'Not marked', value: a.not_marked, color: colors.faint },
            ]}
          />
        </Card>
        <Card title="On leave today" flush actions={canLeave ? <Button size="sm" variant="ghost" title="Calendar" iconRight="chevronRight" onPress={() => go('TeamLeave')} /> : undefined}>
          {d.on_leave_today.length ? (
            d.on_leave_today.map((e, i) => <ListItem key={e.id} left={<Avatar name={e.full_name} />} title={e.full_name} subtitle={e.designation} divider={i < d.on_leave_today.length - 1} />)
          ) : (
            <EmptyState icon="users" title="Everyone is in today" />
          )}
        </Card>
      </Columns>
    </Screen>
  );
}
